import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import StandardSeal from "@/components/StandardSeal";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { StandardSealStill } from "@/components/cinematic/stills";
import api from "@/lib/api";

/**
 * /standard — "The DT6 Standard" public page.
 *
 * Composition per Brand Addendum §3:
 * 1. Masthead — Standard Seal + headline "America First. America Only."
 * 2. Declaration (verbatim from /api/alignment/criteria)
 * 3. Four criteria (rendered from config rows)
 * 4. How vetting works
 * 5. Live declined counter — honest zero at launch
 * 6. Counsel footer note (verbatim)
 */
export default function StandardPage() {
  const [criteria, setCriteria] = useState([]);
  const [declaration, setDeclaration] = useState("");
  const [counselFooter, setCounselFooter] = useState("");
  const [declined, setDeclined] = useState(null);

  useEffect(() => {
    api.get("/alignment/criteria").then((r) => {
      setCriteria(r.data.criteria || []);
      setDeclaration(r.data.declaration || "");
      setCounselFooter(r.data.counsel_footer || "");
    }).catch(() => {});
    api.get("/alignment/counter").then((r) => setDeclined(r.data.declined)).catch(() => {});
  }, []);

  return (
    <>
      <PublicNav />

      {/* Masthead — cinematic Standard Seal backdrop.  Least motion of any
          scene: the page is the oath, not the show. */}
      <div className="relative dt6-flag-wave dt6-cinematic-vignette" style={{ minHeight: "560px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/StandardSealScene")}
          stillSrc={StandardSealStill}
          testId="standard-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section
          title={undefined}
          eyebrow={undefined}
          className="relative dt6-cinematic-copy"
          testId="standard-masthead"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8 pt-4 pb-8">
            <div className="dt6-scale-in">
              <StandardSeal size={180} testId="standard-seal-masthead" />
            </div>
          <div>
            <div className="dt6-chip dt6-chip-primary mb-4 dt6-fade-up" style={{ animationDelay: "180ms" }}>/ THE DT6 STANDARD</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight dt6-fade-up" style={{ animationDelay: "260ms" }}>
              America First.<br />
              <span className="text-[color:var(--dt6-primary)]">America Only.</span>
            </h1>
            <div className="dt6-stripe-underline dt6-fade-up" style={{ maxWidth: 360, marginTop: 16, animationDelay: "360ms" }} />
          </div>
        </div>
      </Section>
      </div>

      {/* Declaration */}
      <Section eyebrow="/ THE DECLARATION" testId="standard-declaration">
        <blockquote
          className="dt6-card p-8 text-lg leading-relaxed border-l-4 dt6-fade-up"
          style={{ borderLeftColor: "var(--dt6-primary)", animationDelay: "440ms" }}
          data-testid="standard-declaration-text"
        >
          {declaration || "…"}
        </blockquote>
      </Section>

      {/* Four criteria */}
      <Section eyebrow="/ THE FOUR CRITERIA" title="What we require" testId="standard-criteria">
        <ul className="dt6-stars grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="standard-criteria-list">
          {criteria.map((c, i) => (
            <li
              key={c.id}
              className="dt6-card p-6 dt6-fade-up"
              style={{ animationDelay: `${560 + i * 110}ms` }}
              data-testid={`criterion-${c.id}`}
            >
              <div className="font-heading text-xl font-semibold mb-2">{c.title}</div>
              <p className="text-sm text-[color:var(--dt6-muted)] leading-relaxed">{c.body}</p>
            </li>
          ))}
        </ul>
      </Section>

      {/* How vetting works */}
      <Section eyebrow="/ HOW VETTING WORKS" title="Apply · Review · Approve" testId="standard-process">
        <ol className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Apply", b: "Submit your mission, funding attestation, and Standard signature." },
            { n: "02", t: "Review", b: "A DT6 principal reviews your organization against the four criteria." },
            { n: "03", t: "Approve", b: "Explicit approval unlocks the arsenal and enables checkout." },
            { n: "04", t: "Accountability", b: "DT6 may refuse or terminate any org that ceases to meet the Standard, with orderly data return." },
          ].map((s) => (
            <li key={s.n} className="dt6-card p-5" data-testid={`process-step-${s.n}`}>
              <div className="font-mono text-xs text-[color:var(--dt6-primary)] mb-2">/ {s.n}</div>
              <div className="font-heading text-lg font-semibold mb-1">{s.t}</div>
              <p className="text-xs text-[color:var(--dt6-muted)]">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Live declined counter (honest zero at launch) */}
      <Section testId="standard-counter">
        <div className="dt6-card p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
             style={{ borderColor: "var(--dt6-primary)" }}>
          <div>
            <div className="font-mono text-xs text-[color:var(--dt6-primary)] mb-2">/ ACCOUNTABILITY, PUBLIC</div>
            <div className="font-heading text-3xl font-bold" data-testid="declined-counter">
              Applications declined to date: <span className="text-[color:var(--dt6-primary)]">{declined ?? "…"}</span>
            </div>
          </div>
          <Link to="/alignment/apply" className="dt6-btn dt6-btn-primary" data-testid="standard-apply-cta">
            Apply to be armed <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* Counsel footer — verbatim */}
      <Section testId="standard-counsel-footer">
        <p className="text-xs text-[color:var(--dt6-muted)] italic max-w-3xl" data-testid="standard-counsel-footer-text">
          {counselFooter || "…"}
        </p>
      </Section>

      <PublicFooter />
    </>
  );
}
